import React, { useState, useRef } from "react";
import { Box, Popover, Typography, alpha, useTheme } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { fileKind } from "./logisticaUtils";

const THUMB = 52;
const PREVIEW_IMG_MAX = 420;
const PREVIEW_PDF_H = 480;

/**
 * Miniatura con vista ampliada al pasar el ratón (imagen o PDF en iframe).
 */
export default function LogisticaDocThumbnail({ url, fileName, label }) {
  const theme = useTheme();
  const kind = fileKind(fileName);
  const [anchor, setAnchor] = useState(null);
  const leaveTimer = useRef(null);

  const openPreview = (el) => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setAnchor(el);
  };

  const scheduleClose = () => {
    leaveTimer.current = setTimeout(() => setAnchor(null), 120);
  };

  const cancelClose = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  };

  const thumbContent =
    kind === "image" ? (
      <Box
        component="img"
        src={url}
        alt={fileName || label || "documento"}
        sx={{
          width: THUMB,
          height: THUMB,
          objectFit: "cover",
          display: "block",
        }}
      />
    ) : (
      <Box
        sx={{
          width: THUMB,
          height: THUMB,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color:
            kind === "pdf"
              ? theme.palette.error.main
              : theme.palette.text.secondary,
          backgroundColor: alpha(
            kind === "pdf"
              ? theme.palette.error.main
              : theme.palette.text.primary,
            0.08,
          ),
        }}
      >
        {kind === "pdf" ? (
          <PictureAsPdfOutlinedIcon sx={{ fontSize: 28 }} />
        ) : (
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 26 }} />
        )}
      </Box>
    );

  return (
    <>
      <Box
        onMouseEnter={(e) => openPreview(e.currentTarget)}
        onMouseLeave={scheduleClose}
        sx={{
          width: THUMB,
          height: THUMB,
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          cursor: "zoom-in",
          boxShadow: anchor ? 2 : 0,
          transition: "box-shadow 0.15s ease",
        }}
      >
        {thumbContent}
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: {
              pointerEvents: "auto",
              p: 1,
              borderRadius: 2,
              maxWidth: "min(92vw, 520px)",
              boxShadow: 8,
            },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        marginThreshold={8}
      >
        {label ? (
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ display: "block", mb: 0.75, px: 0.5 }}
          >
            {label}
          </Typography>
        ) : null}
        {kind === "image" ? (
          <Box
            component="img"
            src={url}
            alt={fileName || ""}
            sx={{
              display: "block",
              maxWidth: PREVIEW_IMG_MAX,
              maxHeight: "70vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        ) : kind === "pdf" ? (
          <Box
            component="iframe"
            src={`${url}#toolbar=0&navpanes=0`}
            title={fileName || "PDF"}
            sx={{
              width: "min(480px, 85vw)",
              height: PREVIEW_PDF_H,
              maxHeight: "70vh",
              border: "none",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            Vista previa no disponible
          </Typography>
        )}
      </Popover>
    </>
  );
}
